import React, { createContext, useContext, useState, useEffect } from 'react';

export type Language = 'he' | 'en' | 'ar';

interface LocaleContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  direction: 'rtl' | 'ltr';
}

const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

const STORAGE_KEY = 'orgchart-language';

// Translation data
const translations: Record<Language, Record<string, string>> = {
  he: {
    // Language names
    'language.hebrew': 'עברית',
    'language.english': 'English',
    'language.arabic': 'العربية',

    // Navigation
    'nav.employees': 'עובדים',
    'nav.admin': 'ניהול',
    'nav.logout': 'התנתק',

    // Controls
    'controls.export': 'ייצוא',
    'controls.print': 'הדפסה / PDF',
    'controls.image': 'תמונה (PNG)',
    'controls.vector': 'וקטור (SVG)',
    'controls.exportJson': 'ייצוא JSON',
    'controls.importJson': 'ייבוא JSON',
    'controls.createFromPdf': 'יצירה מ-PDF (AI)',
    'controls.processing': 'מעבד...',
    'controls.allDivisions': 'כל החטיבות',
    'controls.allDepartments': 'כל המחלקות',
    'controls.filterByDepartment': 'סנן לפי מחלקה',
    'controls.executives': 'הנהלה',
    'controls.zoom': 'זום',
    'controls.textSize': 'גודל טקסט',
    'controls.divisionWidth': 'רוחב חטיבה',
    'controls.filterByDivision': 'סנן לפי חטיבה',
    'controls.toggleActions': 'הצג/הסתר פעולות',
    'controls.layout': 'תצוגה',
    'controls.fitToScreen': 'התאם למסך',
    'controls.companySetup': 'הגדרות חברה',
    'controls.editMode': 'מצב עריכה',
    'controls.viewerNoEdit': 'אין לך הרשאות עריכה',
    'controls.showDetails': 'הצג פרטי תפקיד',
    'controls.showFunctions': 'הצג תפקידים',
    'controls.showSections': 'הצג מדורים',
    'controls.personnel': 'עובדים',
    'controls.orgBoard': 'לוח ארגוני',
    'controls.switchToPersonnel': 'עבור לתצוגת עובדים',
    'controls.switchToOrgBoard': 'עבור ללוח הארגוני',

    // Messages
    'msg.noOrganization': 'לא משויך לארגון',
    'msg.noOrganizationDesc': 'אינך משויך לאף ארגון. אנא פנה למנהל המערכת.',
    'msg.loadSuccess': 'נטען בהצלחה!',
    'msg.loadError': 'שגיאה בטעינת הקובץ',
    'msg.createSuccess': 'לוח התארגנות נוצר בהצלחה!',
    'msg.createError': 'שגיאה ביצירת לוח התארגנות',
    'msg.savedSuccess': 'נשמר בהצלחה!',
    'msg.deleteConfirm': 'האם אתה בטוח שברצונך למחוק?',
    'msg.processingPdf': 'מעבד את ה-PDF עם AI',
    'msg.analyzingStructure': 'מנתח מבנה ארגוני...',
    'msg.extractingData': 'מחלץ נתונים...',
    'msg.buildingOrgBoard': 'בונה לוח התארגנות...',
    'msg.finishingUp': 'מסיים...',

    // Board elements
    'board.clickToEdit': 'לחץ לעריכה',
    'board.editSection': 'לחץ לעריכת מדור',
    'board.department': 'מחלקה',
    'board.section': 'מדור',
    'board.addSection': '+ מדור',
    'board.vfp': 'מוצר סופי בעל ערך:',
    'board.division': 'חטיבה',
    'board.function': 'תפקיד',
    'board.addFunction': '+ תפקיד',

    // Executive modal
    'exec.settings': 'הגדרות הנהלה',
    'exec.owner': 'בעלים',
    'exec.ceo': 'מנכ"ל',
    'exec.deputies': 'סמנכ"לים',
    'exec.companyInfo': 'פרטי החברה',
    'exec.companyName': 'שם החברה',
    'exec.companyVfp': 'מוצר סופי בעל ערך של החברה',
    'exec.executiveFunctions': 'תפקידי הנהלה',
    'exec.addExecutive': '+ הוסף תפקיד הנהלה',
    'exec.functionName': 'שם התפקיד',
    'exec.responsibilities': 'אחריות',
    'exec.addResponsibility': '+ הוסף אחריות',

    // Employees page
    'employees.title': 'רשימת עובדים',
    'employees.total': 'סה"כ: {count} עובדים',
    'employees.add': '+ הוסף עובד',
    'employees.back': 'חזרה ללוח התארגנות',
    'employees.name': 'שם',
    'employees.position': 'תפקיד',
    'employees.department': 'מחלקה',
    'employees.division': 'חטיבה',
    'employees.status': 'סטטוס',
    'employees.active': 'פעיל',
    'employees.inactive': 'לא פעיל',
    'employees.actions': 'פעולות',
    'employees.search': 'חיפוש עובדים...',
    'employees.noEmployees': 'לא נמצאו עובדים',
    'employees.addNew': 'הוסף עובד חדש',
    'employees.editEmployee': 'עריכת עובד',
    'employees.markActive': 'לחץ לסימון פעיל',
    'employees.markInactive': 'לחץ לסימון לא פעיל',
    'employees.shown': 'מוצגים',
    'employees.email': 'אימייל',
    'employees.phone': 'טלפון',
    'employees.number': 'מס\'',
    'employees.assignedRoles': 'תפקידים מוקצים',
    'employees.selectDivision': '-- בחר חטיבה --',
    'employees.namePlaceholder': 'שם העובד',
    'employees.roleNote': '* הקצאת תפקידים מתבצעת דרך לוח ההתארגנות',
    'employees.addEmployee': 'הוסף עובד',
    'employees.divisionManager': 'מנהל חטיבת {name}',
    'employees.departmentManager': 'מנהל מחלקת {name}',
    'employees.vicePresident': 'סמנכ״ל {name}',

    // Inline edit popover
    'edit.title': 'עריכה',
    'edit.name': 'שם',
    'edit.number': 'מספר',
    'edit.vfp': 'מוצר סופי בעל ערך',
    'edit.icon': 'אייקון',
    'edit.employee': 'עובד',
    'edit.selectEmployee': '+ בחר עובד',
    'edit.selectExistingEmployee': '+ בחר עובד קיים',
    'edit.noEmployee': 'ללא עובד',
    'edit.responsibilities': 'אחריות',
    'edit.addResponsibility': '+ הוסף אחריות',
    'edit.deleteSection': 'מחק מדור',
    'edit.deleteFunction': 'מחק תפקיד',
    'edit.deleteDepartment': 'מחק מחלקה',
    'edit.deleteDivision': 'מחק חטיבה',
    'edit.editExecFunction': 'עריכת {name}',
    'edit.editDivision': 'עריכת אגף {number}',
    'edit.editUnit': 'עריכת יח\' {number}',
    'edit.editSection': 'עריכת מדור',
    'edit.newUnit': 'יחידה חדשה',
    'edit.newSection': 'מדור חדש',
    'edit.new': 'חדש',
    'edit.assignedTo': 'ממונה בתפקיד',
    'edit.notAssigned': 'לא מוקצה',
    'edit.employeeName': 'שם העובד',
    'edit.createAndAdd': 'צור והוסף',
    'edit.manager': 'מנהל',
    'edit.managerName': 'שם המנהל',
    'edit.createAsManager': 'צור והגדר כמנהל',
    'edit.selectManager': '+ בחר מנהל',
    'edit.createNewEmployee': 'צור עובד חדש',
    'edit.createNewManager': 'צור מנהל חדש',
    'edit.newEmployee': '+ חדש',
    'edit.managedDivisions': 'חטיבות מנוהלות',
    'edit.responsible': 'אחראי',
    'edit.mainActions': 'פעולות עיקריות',
    'edit.action': 'פעולה',
    'edit.addAction': '+ הוסף פעולה',
    'edit.moveUp': 'הזז למעלה',
    'edit.moveDown': 'הזז למטה',
    'edit.moveToSection': 'העבר למדור אחר',
    'edit.selectSection': 'בחר מדור...',
    'edit.unit': 'יח\'',
    'edit.confirmDelete': 'אישור מחיקה',
    'edit.deleteWarning': 'האם אתה בטוח שברצונך למחוק את {type} "{name}"?',
    'edit.cannotUndo': 'פעולה זו לא ניתנת לביטול.',
    'edit.typeRole': 'התפקיד',
    'edit.typeUnit': 'היחידה',
    'edit.typeSection': 'המדור',
    'edit.create': 'צור',
    'edit.notFound': 'לא נמצא',
    'edit.execFunction': 'תפקיד הנהלה',
    'edit.finalProduct': 'תוצר סופי',
    'edit.color': 'צבע',

    // Stats panel
    'stats.totalEmployees': 'סה״כ עובדים',
    'stats.shown': 'מוצגים',
    'stats.divisions': 'חטיבות',
    'stats.departments': 'מחלקות',

    // Legend
    'legend.divisions': '7 חטיבות',
    'legend.title': 'מקרא',
    'legend.cannotHideExecutive': 'לא ניתן להסתיר את ההנהלה',
    'legend.clickToToggle': 'לחץ להסתיר/להציג {name}',
    'legend.division': 'חטיבה {number}',

    // Tooltip
    'tooltip.division': 'חטיבה',
    'tooltip.department': 'מחלקה',
    'tooltip.section': 'מדור',
    'tooltip.function': 'תפקיד',
    'tooltip.employee': 'עובד',
    'tooltip.vfp': 'מוצר סופי בעל ערך',
    'tooltip.product': 'תוצר',
    'tooltip.notAvailable': 'לא זמין',
    'tooltip.directManager': 'מנהל ישיר',
    'tooltip.email': 'אימייל',
    'tooltip.phone': 'טלפון',

    // General
    'general.save': 'שמור',
    'general.cancel': 'ביטול',
    'general.delete': 'מחק',
    'general.edit': 'ערוך',
    'general.add': 'הוסף',
    'general.confirm': 'אישור',
    'general.loading': 'טוען...',
    'general.close': 'סגור',
    'general.yes': 'כן',
    'general.no': 'לא',
    'general.search': 'חיפוש',
    'general.filter': 'סינון',
    'general.all': 'הכל',
    'general.none': 'ללא',
    'general.select': 'בחר',
    'general.required': 'שדה חובה',
    'general.done': 'סיום',
    'general.remove': 'הסר',
    'general.retry': 'נסה שוב',

    // Firm Dashboard
    'firm.dashboard': 'לוח בקרה',
    'firm.viewingFirm': 'צפייה בפירמה:',
    'firm.backToBoard': '← חזרה ללוח',
    'firm.clientOrganizations': 'ארגוני לקוחות',
    'firm.consultants': 'יועצים',
    'firm.firmLead': 'מנהל פירמה',
    'firm.firmAdmin': 'מנהל פירמה',
    'firm.platformAdmin': 'מנהל מערכת',
    'firm.newClient': '+ לקוח חדש',
    'firm.addConsultant': '+ הוסף יועץ',
    'firm.changeLead': 'שנה מנהל',
    'firm.openBoard': 'פתח לוח',
    'firm.settings': 'הגדרות',
    'firm.users': 'משתמשים',
    'firm.clone': 'שכפל',
    'firm.assignments': 'הקצאות',
    'firm.assignedClients': 'לקוחות מוקצים',
    'firm.noConsultants': 'אין יועצים עדיין. הוסף אחד כדי לעזור בניהול ארגוני הלקוחות.',
    'firm.noClients': 'אין ארגוני לקוחות עדיין.',
    'firm.noFirms': 'אין פירמות ייעוץ עדיין.',
    'firm.createInAdmin': 'צור אחת בלוח הניהול',
    'firm.notAssociated': 'אינך משויך לפירמת ייעוץ.',
    'firm.orgName': 'שם הארגון',
    'firm.language': 'שפה',
    'firm.create': 'צור',
    'firm.emailAddress': 'כתובת אימייל',
    'firm.nameOptional': 'שם (אופציונלי)',
    'firm.loadingDashboard': 'טוען לוח בקרה...',
    'firm.usersFor': 'משתמשים - {name}',
    'firm.addUser': '+ הוסף משתמש',
    'firm.noUsers': 'אין משתמשים בארגון זה עדיין.',
    'firm.role': 'תפקיד',
    'firm.orgAdmin': 'מנהל ארגון',
    'firm.orgUser': 'משתמש ארגון',
    'firm.viewer': 'צופה',
    'firm.cloneOrg': 'שכפל ארגון',
    'firm.cloneDescription': 'צור עותק של "{name}" עם שם חדש.',
    'firm.newOrgName': 'שם ארגון חדש',
    'firm.includeEmployees': 'כלול עובדים',
    'firm.cloning': 'משכפל...',
    'firm.assignClients': 'הקצה לקוחות',
    'firm.assignClientsTo': 'הקצה לקוחות ל-{name}',
    'firm.selectClients': 'בחר אילו ארגוני לקוחות יועץ זה יכול לנהל:',
    'firm.saving': 'שומר...',
    'firm.editConsultant': 'עריכת יועץ',
    'firm.consultantName': 'שם היועץ',
    'firm.changeFirmLead': 'שינוי מנהל פירמה',
    'firm.newLeadEmail': 'אימייל מנהל חדש',
    'firm.changing': 'משנה...',
    'firm.orgSettings': 'הגדרות ארגון',
    'firm.settingsFor': 'הגדרות עבור {name}',
    'firm.branding': 'מיתוג',
    'firm.emoji': 'אימוג\'י',
    'firm.logoUrl': 'כתובת לוגו',
    'firm.deleteOrg': 'מחק ארגון',
    'firm.deleteOrgConfirm': 'האם אתה בטוח? פעולה זו לא ניתנת לביטול.',
    'firm.deleting': 'מוחק...',
    'firm.pending': 'ממתין',
    'firm.created': 'נוצר',
    'firm.updated': 'עודכן',
    'firm.addUserError': 'שגיאה בהוספת משתמש',
    'firm.removeUserError': 'שגיאה בהסרת משתמש',
    'firm.updateRoleError': 'שגיאה בעדכון תפקיד',
    'firm.confirmRemoveUser': 'האם אתה בטוח שברצונך להסיר משתמש זה?',

    // Admin Page
    'admin.dashboard': 'לוח ניהול',
    'admin.backToOrgBoard': '← חזרה ללוח התארגנות',
    'admin.organizations': 'ארגונים',
    'admin.newOrganization': '+ ארגון חדש',
    'admin.consultingFirms': 'פירמות ייעוץ',
    'admin.newFirm': '+ פירמה חדשה',
    'admin.userManagement': 'ניהול משתמשים',
    'admin.newUser': '+ משתמש חדש',
    'admin.accessDenied': 'הגישה נדחתה',
    'admin.noPermission': 'אין לך הרשאה לגשת לעמוד זה. רק מנהלי ארגון יכולים לנהל משתמשים.',
    'admin.goToOrgBoard': 'לך ללוח התארגנות',
    'admin.noOrganizations': 'אין ארגונים עדיין.',
    'admin.firmName': 'שם הפירמה',
    'admin.firmLeadEmail': 'אימייל מנהל הפירמה',
    'admin.creating': 'יוצר...',
    'admin.noFirms': 'אין פירמות ייעוץ עדיין.',
    'admin.manageFirm': 'נהל',
    'admin.clients': 'לקוחות',
    'admin.lead': 'מנהל',
    'admin.userEmail': 'אימייל משתמש',
    'admin.userName': 'שם משתמש',
    'admin.userRole': 'תפקיד',
    'admin.userOrg': 'ארגון (אופציונלי)',
    'admin.noUsers': 'אין משתמשים עדיין.',
    'admin.selectOrg': 'בחר ארגון',
    'admin.allOrgs': 'כל הארגונים',
    'admin.filterByOrg': 'סנן לפי ארגון',
    'admin.addUser': 'הוסף משתמש',
    'admin.name': 'שם',
    'admin.email': 'אימייל',
    'admin.role': 'תפקיד',
    'admin.organization': 'ארגון',
    'admin.actions': 'פעולות',
    'admin.noUsersFound': 'לא נמצאו משתמשים',
    'admin.selectOrgOrAdd': 'בחר ארגון או הוסף משתמש.',
    'admin.pending': 'ממתין',
    'admin.deleteUserConfirm': 'האם אתה בטוח שברצונך למחוק משתמש זה?',
    'admin.deletePendingConfirm': 'האם אתה בטוח שברצונך למחוק הזמנה זו?',
    'admin.deleteOrgConfirm': 'האם אתה בטוח שברצונך למחוק ארגון זה? כל המשתמשים והנתונים יאבדו.',
    'admin.deleteFirmConfirm': 'האם אתה בטוח שברצונך למחוק פירמת ייעוץ זו? כל היועצים יהפכו לצופים.',
    'admin.assignToFirm': 'הקצה לפירמת ייעוץ',
    'admin.noFirmAssigned': 'ללא פירמה',
    'admin.cloneOrganization': 'שכפול ארגון',
    'admin.cloneSource': 'משכפל:',
    'admin.newOrgName': 'שם ארגון חדש',
    'admin.enterNewName': 'הזן שם חדש',
    'admin.includeEmployees': 'כלול עובדים',
    'admin.includeEmployeesHelp': 'העובדים יועתקו עם מזהים חדשים. כל ההקצאות והפניות למנהלים יישמרו.',
    'admin.excludeEmployeesHelp': 'רק מבנה הארגון (חטיבות, מחלקות, מדורים, תפקידים) יועתק. ללא עובדים או הקצאות.',
    'admin.cloning': 'משכפל...',
    'admin.cloneAction': 'שכפל ארגון',
    'admin.roleUser': 'משתמש',
    'admin.roleOrgAdmin': 'מנהל ארגון',
    'admin.roleViewer': 'צופה',
    'admin.rolePlatformAdmin': 'מנהל מערכת',
    'admin.created': 'נוצר',
    'admin.updated': 'עודכן',
  },
  en: {
    // Language names
    'language.hebrew': 'עברית',
    'language.english': 'English',
    'language.arabic': 'العربية',

    // Navigation
    'nav.employees': 'Employees',
    'nav.admin': 'Admin',
    'nav.logout': 'Logout',

    // Controls
    'controls.export': 'Export',
    'controls.print': 'Print / PDF',
    'controls.image': 'Image (PNG)',
    'controls.vector': 'Vector (SVG)',
    'controls.exportJson': 'Export JSON',
    'controls.importJson': 'Import JSON',
    'controls.createFromPdf': 'Create from PDF (AI)',
    'controls.processing': 'Processing...',
    'controls.allDivisions': 'All Divisions',
    'controls.allDepartments': 'All Departments',
    'controls.filterByDepartment': 'Filter by Department',
    'controls.executives': 'Executives',
    'controls.zoom': 'Zoom',
    'controls.textSize': 'Text Size',
    'controls.divisionWidth': 'Division Width',
    'controls.filterByDivision': 'Filter by Division',
    'controls.toggleActions': 'Show/Hide Actions',
    'controls.layout': 'Layout',
    'controls.fitToScreen': 'Fit to Screen',
    'controls.companySetup': 'Company Setup',
    'controls.editMode': 'Edit Mode',
    'controls.viewerNoEdit': 'You do not have edit permissions',
    'controls.showDetails': 'Show Function Details',
    'controls.showFunctions': 'Show Functions',
    'controls.showSections': 'Show Sections',
    'controls.personnel': 'Personnel',
    'controls.orgBoard': 'Org Board',
    'controls.switchToPersonnel': 'Switch to Personnel Chart',
    'controls.switchToOrgBoard': 'Switch to Org Board',

    // Messages
    'msg.noOrganization': 'No Organization',
    'msg.noOrganizationDesc': 'You are not assigned to any organization. Please contact an administrator.',
    'msg.loadSuccess': 'Loaded successfully!',
    'msg.loadError': 'Error loading file',
    'msg.createSuccess': 'Org board created successfully!',
    'msg.createError': 'Error creating org board',
    'msg.savedSuccess': 'Saved successfully!',
    'msg.deleteConfirm': 'Are you sure you want to delete?',
    'msg.processingPdf': 'Processing PDF with AI',
    'msg.analyzingStructure': 'Analyzing organizational structure...',
    'msg.extractingData': 'Extracting data...',
    'msg.buildingOrgBoard': 'Building org board...',
    'msg.finishingUp': 'Finishing up...',

    // Board elements
    'board.clickToEdit': 'Click to edit',
    'board.editSection': 'Click to edit section',
    'board.department': 'Department',
    'board.section': 'Section',
    'board.addSection': '+ Section',
    'board.vfp': 'Valuable Final Product:',
    'board.division': 'Division',
    'board.function': 'Post',
    'board.addFunction': '+ Post',

    // Executive modal
    'exec.settings': 'Executive Settings',
    'exec.owner': 'Owner',
    'exec.ceo': 'CEO',
    'exec.deputies': 'Deputies',
    'exec.companyInfo': 'Company Information',
    'exec.companyName': 'Company Name',
    'exec.companyVfp': 'Company Valuable Final Product',
    'exec.executiveFunctions': 'Executive Functions',
    'exec.addExecutive': '+ Add Executive Function',
    'exec.functionName': 'Function Name',
    'exec.responsibilities': 'Responsibilities',
    'exec.addResponsibility': '+ Add Responsibility',

    // Employees page
    'employees.title': 'Employee List',
    'employees.total': 'Total: {count} employees',
    'employees.add': '+ Add Employee',
    'employees.back': 'Back to Org Board',
    'employees.name': 'Name',
    'employees.position': 'Position',
    'employees.department': 'Department',
    'employees.division': 'Division',
    'employees.status': 'Status',
    'employees.active': 'Active',
    'employees.inactive': 'Inactive',
    'employees.actions': 'Actions',
    'employees.search': 'Search employees...',
    'employees.noEmployees': 'No employees found',
    'employees.addNew': 'Add New Employee',
    'employees.editEmployee': 'Edit Employee',
    'employees.markActive': 'Click to mark as active',
    'employees.markInactive': 'Click to mark as inactive',
    'employees.shown': 'Shown',
    'employees.email': 'Email',
    'employees.phone': 'Phone',
    'employees.number': '#',
    'employees.assignedRoles': 'Assigned Roles',
    'employees.selectDivision': '-- Select Division --',
    'employees.namePlaceholder': 'Employee name',
    'employees.roleNote': '* Role assignments are done via the org board',
    'employees.addEmployee': 'Add Employee',
    'employees.divisionManager': '{name} Division Manager',
    'employees.departmentManager': '{name} Department Manager',
    'employees.vicePresident': 'VP of {name}',

    // Inline edit popover
    'edit.title': 'Edit',
    'edit.name': 'Name',
    'edit.number': 'Number',
    'edit.vfp': 'Valuable Final Product',
    'edit.icon': 'Icon',
    'edit.employee': 'Employee',
    'edit.selectEmployee': '+ Select Employee',
    'edit.selectExistingEmployee': '+ Select Existing Employee',
    'edit.noEmployee': 'No Employee',
    'edit.responsibilities': 'Responsibilities',
    'edit.addResponsibility': '+ Add Responsibility',
    'edit.deleteSection': 'Delete Section',
    'edit.deleteFunction': 'Delete Function',
    'edit.deleteDepartment': 'Delete Department',
    'edit.deleteDivision': 'Delete Division',
    'edit.editExecFunction': 'Edit {name}',
    'edit.editDivision': 'Edit Division {number}',
    'edit.editUnit': 'Edit Unit {number}',
    'edit.editSection': 'Edit Section',
    'edit.newUnit': 'New Unit',
    'edit.newSection': 'New Section',
    'edit.new': 'New',
    'edit.assignedTo': 'Assigned To',
    'edit.notAssigned': 'Not Assigned',
    'edit.employeeName': 'Employee Name',
    'edit.createAndAdd': 'Create & Add',
    'edit.manager': 'Manager',
    'edit.managerName': 'Manager Name',
    'edit.createAsManager': 'Create & Set as Manager',
    'edit.selectManager': '+ Select Manager',
    'edit.createNewEmployee': 'Create New Employee',
    'edit.createNewManager': 'Create New Manager',
    'edit.newEmployee': '+ New',
    'edit.managedDivisions': 'Managed Divisions',
    'edit.responsible': 'Responsible',
    'edit.mainActions': 'Main Actions',
    'edit.action': 'Action',
    'edit.addAction': '+ Add Action',
    'edit.moveUp': 'Move Up',
    'edit.moveDown': 'Move Down',
    'edit.moveToSection': 'Move to Another Section',
    'edit.selectSection': 'Select Section...',
    'edit.unit': 'Unit',
    'edit.confirmDelete': 'Confirm Delete',
    'edit.deleteWarning': 'Are you sure you want to delete {type} "{name}"?',
    'edit.cannotUndo': 'This action cannot be undone.',
    'edit.typeRole': 'the role',
    'edit.typeUnit': 'the unit',
    'edit.typeSection': 'the section',
    'edit.create': 'Create',
    'edit.notFound': 'Not Found',
    'edit.execFunction': 'Executive Function',
    'edit.finalProduct': 'Final Product',
    'edit.color': 'Color',

    // Stats panel
    'stats.totalEmployees': 'Total Employees',
    'stats.shown': 'Shown',
    'stats.divisions': 'Divisions',
    'stats.departments': 'Departments',

    // Legend
    'legend.divisions': '7 Divisions',
    'legend.title': 'Legend',
    'legend.cannotHideExecutive': 'Cannot hide executives',
    'legend.clickToToggle': 'Click to show/hide {name}',
    'legend.division': 'Division {number}',

    // Tooltip
    'tooltip.division': 'Division',
    'tooltip.department': 'Department',
    'tooltip.section': 'Section',
    'tooltip.function': 'Function',
    'tooltip.employee': 'Employee',
    'tooltip.vfp': 'Valuable Final Product',
    'tooltip.product': 'Product',
    'tooltip.notAvailable': 'Not available',
    'tooltip.directManager': 'Direct Manager',
    'tooltip.email': 'Email',
    'tooltip.phone': 'Phone',

    // General
    'general.save': 'Save',
    'general.cancel': 'Cancel',
    'general.delete': 'Delete',
    'general.edit': 'Edit',
    'general.add': 'Add',
    'general.confirm': 'Confirm',
    'general.loading': 'Loading...',
    'general.close': 'Close',
    'general.yes': 'Yes',
    'general.no': 'No',
    'general.search': 'Search',
    'general.filter': 'Filter',
    'general.all': 'All',
    'general.none': 'None',
    'general.select': 'Select',
    'general.required': 'Required field',
    'general.done': 'Done',
    'general.remove': 'Remove',
    'general.retry': 'Retry',

    // Firm Dashboard
    'firm.dashboard': 'Dashboard',
    'firm.viewingFirm': 'Viewing Firm:',
    'firm.backToBoard': '← Back to Board',
    'firm.clientOrganizations': 'Client Organizations',
    'firm.consultants': 'Consultants',
    'firm.firmLead': 'Firm Lead',
    'firm.firmAdmin': 'Firm Administrator',
    'firm.platformAdmin': 'Platform Admin',
    'firm.newClient': '+ New Client',
    'firm.addConsultant': '+ Add Consultant',
    'firm.changeLead': 'Change Lead',
    'firm.openBoard': 'Open Board',
    'firm.settings': 'Settings',
    'firm.users': 'Users',
    'firm.clone': 'Clone',
    'firm.assignments': 'Assignments',
    'firm.assignedClients': 'assigned clients',
    'firm.noConsultants': 'No consultants yet. Add one to help manage client organizations.',
    'firm.noClients': 'No client organizations yet.',
    'firm.noFirms': 'No consulting firms exist yet.',
    'firm.createInAdmin': 'Create one in the Admin Dashboard',
    'firm.notAssociated': 'You are not associated with a consulting firm.',
    'firm.orgName': 'Organization Name',
    'firm.language': 'Language',
    'firm.create': 'Create',
    'firm.emailAddress': 'Email address',
    'firm.nameOptional': 'Name (optional)',
    'firm.loadingDashboard': 'Loading dashboard...',
    'firm.usersFor': 'Users - {name}',
    'firm.addUser': '+ Add User',
    'firm.noUsers': 'No users in this organization yet.',
    'firm.role': 'Role',
    'firm.orgAdmin': 'Org Admin',
    'firm.orgUser': 'Org User',
    'firm.viewer': 'Viewer',
    'firm.cloneOrg': 'Clone Organization',
    'firm.cloneDescription': 'Create a copy of "{name}" with a new name.',
    'firm.newOrgName': 'New Organization Name',
    'firm.includeEmployees': 'Include Employees',
    'firm.cloning': 'Cloning...',
    'firm.assignClients': 'Assign Clients',
    'firm.assignClientsTo': 'Assign Clients to {name}',
    'firm.selectClients': 'Select which client organizations this consultant can manage:',
    'firm.saving': 'Saving...',
    'firm.editConsultant': 'Edit Consultant',
    'firm.consultantName': 'Consultant Name',
    'firm.changeFirmLead': 'Change Firm Lead',
    'firm.newLeadEmail': 'New Lead Email',
    'firm.changing': 'Changing...',
    'firm.orgSettings': 'Organization Settings',
    'firm.settingsFor': 'Settings for {name}',
    'firm.branding': 'Branding',
    'firm.emoji': 'Emoji',
    'firm.logoUrl': 'Logo URL',
    'firm.deleteOrg': 'Delete Organization',
    'firm.deleteOrgConfirm': 'Are you sure? This action cannot be undone.',
    'firm.deleting': 'Deleting...',
    'firm.pending': 'Pending',
    'firm.created': 'Created',
    'firm.updated': 'Updated',
    'firm.addUserError': 'Failed to add user',
    'firm.removeUserError': 'Failed to remove user',
    'firm.updateRoleError': 'Failed to update role',
    'firm.confirmRemoveUser': 'Are you sure you want to remove this user?',

    // Admin Page
    'admin.dashboard': 'Admin Dashboard',
    'admin.backToOrgBoard': '← Back to Org Board',
    'admin.organizations': 'Organizations',
    'admin.newOrganization': '+ New Organization',
    'admin.consultingFirms': 'Consulting Firms',
    'admin.newFirm': '+ New Firm',
    'admin.userManagement': 'User Management',
    'admin.newUser': '+ New User',
    'admin.accessDenied': 'Access Denied',
    'admin.noPermission': 'You don\'t have permission to access this page. Only organization admins can manage users.',
    'admin.goToOrgBoard': 'Go to Org Board',
    'admin.noOrganizations': 'No organizations yet.',
    'admin.firmName': 'Firm Name',
    'admin.firmLeadEmail': 'Firm Lead Email',
    'admin.creating': 'Creating...',
    'admin.noFirms': 'No consulting firms yet.',
    'admin.manageFirm': 'Manage',
    'admin.clients': 'clients',
    'admin.lead': 'Lead',
    'admin.userEmail': 'User Email',
    'admin.userName': 'User Name',
    'admin.userRole': 'Role',
    'admin.userOrg': 'Organization (optional)',
    'admin.noUsers': 'No users yet.',
    'admin.selectOrg': 'Select Organization',
    'admin.allOrgs': 'All Organizations',
    'admin.filterByOrg': 'Filter by Organization',
    'admin.addUser': 'Add User',
    'admin.name': 'Name',
    'admin.email': 'Email',
    'admin.role': 'Role',
    'admin.organization': 'Organization',
    'admin.actions': 'Actions',
    'admin.noUsersFound': 'No users found',
    'admin.selectOrgOrAdd': 'Select an organization or add a user.',
    'admin.pending': 'Pending',
    'admin.deleteUserConfirm': 'Are you sure you want to delete this user?',
    'admin.deletePendingConfirm': 'Are you sure you want to delete this pending invitation?',
    'admin.deleteOrgConfirm': 'Are you sure you want to delete this organization? All users and data will be lost.',
    'admin.deleteFirmConfirm': 'Are you sure you want to delete this consulting firm? All consultants will be converted to viewers.',
    'admin.assignToFirm': 'Assign to consulting firm',
    'admin.noFirmAssigned': 'No Firm',
    'admin.cloneOrganization': 'Clone Organization',
    'admin.cloneSource': 'Cloning:',
    'admin.newOrgName': 'New Organization Name',
    'admin.enterNewName': 'Enter new name',
    'admin.includeEmployees': 'Include employees',
    'admin.includeEmployeesHelp': 'Employees will be copied with new IDs. All assignments and manager references will be preserved.',
    'admin.excludeEmployeesHelp': 'Only the org structure (divisions, departments, sections, functions) will be copied. No employees or assignments.',
    'admin.cloning': 'Cloning...',
    'admin.cloneAction': 'Clone Organization',
    'admin.roleUser': 'User',
    'admin.roleOrgAdmin': 'Org Admin',
    'admin.roleViewer': 'Viewer',
    'admin.rolePlatformAdmin': 'Platform Admin',
    'admin.created': 'Created',
    'admin.updated': 'Updated',
  },
  ar: {
    // Language names
    'language.hebrew': 'עברית',
    'language.english': 'English',
    'language.arabic': 'العربية',

    // Navigation
    'nav.employees': 'الموظفون',
    'nav.admin': 'الإدارة',
    'nav.logout': 'تسجيل الخروج',

    // Controls
    'controls.export': 'تصدير',
    'controls.print': 'طباعة / PDF',
    'controls.image': 'صورة (PNG)',
    'controls.vector': 'متجه (SVG)',
    'controls.exportJson': 'تصدير JSON',
    'controls.importJson': 'استيراد JSON',
    'controls.createFromPdf': 'إنشاء من PDF (AI)',
    'controls.processing': 'جاري المعالجة...',
    'controls.allDivisions': 'جميع الأقسام',
    'controls.allDepartments': 'جميع الإدارات',
    'controls.filterByDepartment': 'تصفية حسب الإدارة',
    'controls.executives': 'الإدارة التنفيذية',
    'controls.zoom': 'تكبير',
    'controls.textSize': 'حجم النص',
    'controls.divisionWidth': 'عرض القسم',
    'controls.filterByDivision': 'تصفية حسب القسم',
    'controls.toggleActions': 'إظهار/إخفاء الإجراءات',
    'controls.layout': 'التخطيط',
    'controls.fitToScreen': 'ملاءمة للشاشة',
    'controls.companySetup': 'إعدادات الشركة',
    'controls.editMode': 'وضع التعديل',
    'controls.viewerNoEdit': 'ليس لديك صلاحيات التعديل',
    'controls.showDetails': 'إظهار تفاصيل الوظيفة',
    'controls.showFunctions': 'إظهار الوظائف',
    'controls.showSections': 'إظهار الأقسام',
    'controls.personnel': 'الموظفون',
    'controls.orgBoard': 'لوحة الهيكل',
    'controls.switchToPersonnel': 'التبديل إلى مخطط الموظفين',
    'controls.switchToOrgBoard': 'التبديل إلى لوحة الهيكل',

    // Messages
    'msg.noOrganization': 'لا يوجد مؤسسة',
    'msg.noOrganizationDesc': 'أنت غير مسجل في أي مؤسسة. يرجى الاتصال بالمسؤول.',
    'msg.loadSuccess': 'تم التحميل بنجاح!',
    'msg.loadError': 'خطأ في تحميل الملف',
    'msg.createSuccess': 'تم إنشاء مخطط التنظيم بنجاح!',
    'msg.createError': 'خطأ في إنشاء مخطط التنظيم',
    'msg.savedSuccess': 'تم الحفظ بنجاح!',
    'msg.deleteConfirm': 'هل أنت متأكد أنك تريد الحذف؟',
    'msg.processingPdf': 'معالجة PDF بالذكاء الاصطناعي',
    'msg.analyzingStructure': 'تحليل الهيكل التنظيمي...',
    'msg.extractingData': 'استخراج البيانات...',
    'msg.buildingOrgBoard': 'بناء مخطط التنظيم...',
    'msg.finishingUp': 'إنهاء...',

    // Board elements
    'board.clickToEdit': 'انقر للتعديل',
    'board.editSection': 'انقر لتعديل القسم',
    'board.department': 'قسم',
    'board.section': 'وحدة',
    'board.addSection': '+ وحدة',
    'board.vfp': 'المنتج النهائي القيّم:',
    'board.division': 'شعبة',
    'board.function': 'قبعة',
    'board.addFunction': '+ قبعة',

    // Executive modal
    'exec.settings': 'إعدادات الإدارة',
    'exec.owner': 'المالك',
    'exec.ceo': 'المدير التنفيذي',
    'exec.deputies': 'النواب',
    'exec.companyInfo': 'معلومات الشركة',
    'exec.companyName': 'اسم الشركة',
    'exec.companyVfp': 'المنتج النهائي القيّم للشركة',
    'exec.executiveFunctions': 'الوظائف التنفيذية',
    'exec.addExecutive': '+ إضافة وظيفة تنفيذية',
    'exec.functionName': 'اسم الوظيفة',
    'exec.responsibilities': 'المسؤوليات',
    'exec.addResponsibility': '+ إضافة مسؤولية',

    // Employees page
    'employees.title': 'قائمة الموظفين',
    'employees.total': 'الإجمالي: {count} موظفين',
    'employees.add': '+ إضافة موظف',
    'employees.back': 'العودة إلى مخطط التنظيم',
    'employees.name': 'الاسم',
    'employees.position': 'المنصب',
    'employees.department': 'القسم',
    'employees.division': 'الشعبة',
    'employees.status': 'الحالة',
    'employees.active': 'نشط',
    'employees.inactive': 'غير نشط',
    'employees.actions': 'الإجراءات',
    'employees.search': 'البحث عن موظفين...',
    'employees.noEmployees': 'لم يتم العثور على موظفين',
    'employees.addNew': 'إضافة موظف جديد',
    'employees.editEmployee': 'تعديل الموظف',
    'employees.markActive': 'انقر للتعيين كنشط',
    'employees.markInactive': 'انقر للتعيين كغير نشط',
    'employees.shown': 'معروض',
    'employees.email': 'البريد الإلكتروني',
    'employees.phone': 'الهاتف',
    'employees.number': '#',
    'employees.assignedRoles': 'الأدوار المعينة',
    'employees.selectDivision': '-- اختر الشعبة --',
    'employees.namePlaceholder': 'اسم الموظف',
    'employees.roleNote': '* يتم تعيين الأدوار من خلال مخطط التنظيم',
    'employees.addEmployee': 'إضافة موظف',
    'employees.divisionManager': 'مدير شعبة {name}',
    'employees.departmentManager': 'مدير قسم {name}',
    'employees.vicePresident': 'نائب رئيس {name}',

    // Inline edit popover
    'edit.title': 'تعديل',
    'edit.name': 'الاسم',
    'edit.number': 'الرقم',
    'edit.vfp': 'المنتج النهائي القيّم',
    'edit.icon': 'الأيقونة',
    'edit.employee': 'الموظف',
    'edit.selectEmployee': '+ اختر موظف',
    'edit.selectExistingEmployee': '+ اختر موظف موجود',
    'edit.noEmployee': 'بدون موظف',
    'edit.responsibilities': 'المسؤوليات',
    'edit.addResponsibility': '+ إضافة مسؤولية',
    'edit.deleteSection': 'حذف الوحدة',
    'edit.deleteFunction': 'حذف الوظيفة',
    'edit.deleteDepartment': 'حذف القسم',
    'edit.deleteDivision': 'حذف الشعبة',
    'edit.editExecFunction': 'تعديل {name}',
    'edit.editDivision': 'تعديل الشعبة {number}',
    'edit.editUnit': 'تعديل الوحدة {number}',
    'edit.editSection': 'تعديل القسم',
    'edit.newUnit': 'وحدة جديدة',
    'edit.newSection': 'قسم جديد',
    'edit.new': 'جديد',
    'edit.assignedTo': 'مُعين في',
    'edit.notAssigned': 'غير مُعين',
    'edit.employeeName': 'اسم الموظف',
    'edit.createAndAdd': 'إنشاء وإضافة',
    'edit.manager': 'المدير',
    'edit.managerName': 'اسم المدير',
    'edit.createAsManager': 'إنشاء وتعيين كمدير',
    'edit.selectManager': '+ اختر مدير',
    'edit.createNewEmployee': 'إنشاء موظف جديد',
    'edit.createNewManager': 'إنشاء مدير جديد',
    'edit.newEmployee': '+ جديد',
    'edit.managedDivisions': 'الشعب المُدارة',
    'edit.responsible': 'المسؤول',
    'edit.mainActions': 'الإجراءات الرئيسية',
    'edit.action': 'إجراء',
    'edit.addAction': '+ إضافة إجراء',
    'edit.moveUp': 'نقل للأعلى',
    'edit.moveDown': 'نقل للأسفل',
    'edit.moveToSection': 'نقل إلى قسم آخر',
    'edit.selectSection': 'اختر قسم...',
    'edit.unit': 'وحدة',
    'edit.confirmDelete': 'تأكيد الحذف',
    'edit.deleteWarning': 'هل أنت متأكد أنك تريد حذف {type} "{name}"؟',
    'edit.cannotUndo': 'لا يمكن التراجع عن هذا الإجراء.',
    'edit.typeRole': 'الدور',
    'edit.typeUnit': 'الوحدة',
    'edit.typeSection': 'القسم',
    'edit.create': 'إنشاء',
    'edit.notFound': 'غير موجود',
    'edit.execFunction': 'وظيفة تنفيذية',
    'edit.finalProduct': 'المنتج النهائي',
    'edit.color': 'اللون',

    // Stats panel
    'stats.totalEmployees': 'إجمالي الموظفين',
    'stats.shown': 'معروض',
    'stats.divisions': 'الشعب',
    'stats.departments': 'الأقسام',

    // Legend
    'legend.divisions': '7 شعب',
    'legend.title': 'دليل',
    'legend.cannotHideExecutive': 'لا يمكن إخفاء الإدارة',
    'legend.clickToToggle': 'انقر لإظهار/إخفاء {name}',
    'legend.division': 'شعبة {number}',

    // Tooltip
    'tooltip.division': 'شعبة',
    'tooltip.department': 'قسم',
    'tooltip.section': 'وحدة',
    'tooltip.function': 'وظيفة',
    'tooltip.employee': 'موظف',
    'tooltip.vfp': 'المنتج النهائي القيّم',
    'tooltip.product': 'المنتج',
    'tooltip.notAvailable': 'غير متاح',
    'tooltip.directManager': 'المدير المباشر',
    'tooltip.email': 'البريد الإلكتروني',
    'tooltip.phone': 'الهاتف',

    // General
    'general.save': 'حفظ',
    'general.cancel': 'إلغاء',
    'general.delete': 'حذف',
    'general.edit': 'تعديل',
    'general.add': 'إضافة',
    'general.confirm': 'تأكيد',
    'general.loading': 'جاري التحميل...',
    'general.close': 'إغلاق',
    'general.yes': 'نعم',
    'general.no': 'لا',
    'general.search': 'بحث',
    'general.filter': 'تصفية',
    'general.all': 'الكل',
    'general.none': 'لا شيء',
    'general.select': 'اختر',
    'general.required': 'حقل مطلوب',
    'general.done': 'تم',
    'general.remove': 'إزالة',
    'general.retry': 'إعادة المحاولة',

    // Firm Dashboard
    'firm.dashboard': 'لوحة التحكم',
    'firm.viewingFirm': 'عرض الشركة:',
    'firm.backToBoard': '← العودة إلى اللوحة',
    'firm.clientOrganizations': 'مؤسسات العملاء',
    'firm.consultants': 'المستشارون',
    'firm.firmLead': 'مدير الشركة',
    'firm.firmAdmin': 'مدير الشركة',
    'firm.platformAdmin': 'مدير المنصة',
    'firm.newClient': '+ عميل جديد',
    'firm.addConsultant': '+ إضافة مستشار',
    'firm.changeLead': 'تغيير المدير',
    'firm.openBoard': 'فتح اللوحة',
    'firm.settings': 'الإعدادات',
    'firm.users': 'المستخدمون',
    'firm.clone': 'نسخ',
    'firm.assignments': 'التعيينات',
    'firm.assignedClients': 'عملاء معينون',
    'firm.noConsultants': 'لا يوجد مستشارون بعد. أضف واحداً للمساعدة في إدارة مؤسسات العملاء.',
    'firm.noClients': 'لا توجد مؤسسات عملاء بعد.',
    'firm.noFirms': 'لا توجد شركات استشارية بعد.',
    'firm.createInAdmin': 'أنشئ واحدة في لوحة الإدارة',
    'firm.notAssociated': 'أنت غير مرتبط بشركة استشارية.',
    'firm.orgName': 'اسم المؤسسة',
    'firm.language': 'اللغة',
    'firm.create': 'إنشاء',
    'firm.emailAddress': 'عنوان البريد الإلكتروني',
    'firm.nameOptional': 'الاسم (اختياري)',
    'firm.loadingDashboard': 'جاري تحميل لوحة التحكم...',
    'firm.usersFor': 'المستخدمون - {name}',
    'firm.addUser': '+ إضافة مستخدم',
    'firm.noUsers': 'لا يوجد مستخدمون في هذه المؤسسة بعد.',
    'firm.role': 'الدور',
    'firm.orgAdmin': 'مدير المؤسسة',
    'firm.orgUser': 'مستخدم المؤسسة',
    'firm.viewer': 'مشاهد',
    'firm.cloneOrg': 'نسخ المؤسسة',
    'firm.cloneDescription': 'إنشاء نسخة من "{name}" باسم جديد.',
    'firm.newOrgName': 'اسم المؤسسة الجديد',
    'firm.includeEmployees': 'تضمين الموظفين',
    'firm.cloning': 'جاري النسخ...',
    'firm.assignClients': 'تعيين العملاء',
    'firm.assignClientsTo': 'تعيين العملاء لـ {name}',
    'firm.selectClients': 'اختر مؤسسات العملاء التي يمكن لهذا المستشار إدارتها:',
    'firm.saving': 'جاري الحفظ...',
    'firm.editConsultant': 'تعديل المستشار',
    'firm.consultantName': 'اسم المستشار',
    'firm.changeFirmLead': 'تغيير مدير الشركة',
    'firm.newLeadEmail': 'بريد المدير الجديد',
    'firm.changing': 'جاري التغيير...',
    'firm.orgSettings': 'إعدادات المؤسسة',
    'firm.settingsFor': 'إعدادات {name}',
    'firm.branding': 'العلامة التجارية',
    'firm.emoji': 'رمز تعبيري',
    'firm.logoUrl': 'رابط الشعار',
    'firm.deleteOrg': 'حذف المؤسسة',
    'firm.deleteOrgConfirm': 'هل أنت متأكد؟ لا يمكن التراجع عن هذا الإجراء.',
    'firm.deleting': 'جاري الحذف...',
    'firm.pending': 'قيد الانتظار',
    'firm.created': 'تاريخ الإنشاء',
    'firm.updated': 'آخر تحديث',
    'firm.addUserError': 'فشل في إضافة المستخدم',
    'firm.removeUserError': 'فشل في إزالة المستخدم',
    'firm.updateRoleError': 'فشل في تحديث الدور',
    'firm.confirmRemoveUser': 'هل أنت متأكد أنك تريد إزالة هذا المستخدم؟',

    // Admin Page
    'admin.dashboard': 'لوحة الإدارة',
    'admin.backToOrgBoard': '← العودة إلى مخطط التنظيم',
    'admin.organizations': 'المؤسسات',
    'admin.newOrganization': '+ مؤسسة جديدة',
    'admin.consultingFirms': 'شركات الاستشارات',
    'admin.newFirm': '+ شركة جديدة',
    'admin.userManagement': 'إدارة المستخدمين',
    'admin.newUser': '+ مستخدم جديد',
    'admin.accessDenied': 'تم رفض الوصول',
    'admin.noPermission': 'ليس لديك صلاحية الوصول إلى هذه الصفحة. فقط مديرو المؤسسات يمكنهم إدارة المستخدمين.',
    'admin.goToOrgBoard': 'اذهب إلى مخطط التنظيم',
    'admin.noOrganizations': 'لا توجد مؤسسات بعد.',
    'admin.firmName': 'اسم الشركة',
    'admin.firmLeadEmail': 'بريد مدير الشركة',
    'admin.creating': 'جاري الإنشاء...',
    'admin.noFirms': 'لا توجد شركات استشارية بعد.',
    'admin.manageFirm': 'إدارة',
    'admin.clients': 'عملاء',
    'admin.lead': 'المدير',
    'admin.userEmail': 'بريد المستخدم',
    'admin.userName': 'اسم المستخدم',
    'admin.userRole': 'الدور',
    'admin.userOrg': 'المؤسسة (اختياري)',
    'admin.noUsers': 'لا يوجد مستخدمون بعد.',
    'admin.selectOrg': 'اختر المؤسسة',
    'admin.allOrgs': 'جميع المؤسسات',
    'admin.filterByOrg': 'تصفية حسب المؤسسة',
    'admin.addUser': 'إضافة مستخدم',
    'admin.name': 'الاسم',
    'admin.email': 'البريد الإلكتروني',
    'admin.role': 'الدور',
    'admin.organization': 'المؤسسة',
    'admin.actions': 'الإجراءات',
    'admin.noUsersFound': 'لم يتم العثور على مستخدمين',
    'admin.selectOrgOrAdd': 'اختر مؤسسة أو أضف مستخدم.',
    'admin.pending': 'قيد الانتظار',
    'admin.deleteUserConfirm': 'هل أنت متأكد أنك تريد حذف هذا المستخدم؟',
    'admin.deletePendingConfirm': 'هل أنت متأكد أنك تريد حذف هذه الدعوة؟',
    'admin.deleteOrgConfirm': 'هل أنت متأكد أنك تريد حذف هذه المؤسسة؟ سيتم فقدان جميع المستخدمين والبيانات.',
    'admin.deleteFirmConfirm': 'هل أنت متأكد أنك تريد حذف شركة الاستشارات هذه؟ سيتم تحويل جميع المستشارين إلى مشاهدين.',
    'admin.assignToFirm': 'تعيين لشركة استشارية',
    'admin.noFirmAssigned': 'بدون شركة',
    'admin.cloneOrganization': 'نسخ المؤسسة',
    'admin.cloneSource': 'نسخ:',
    'admin.newOrgName': 'اسم المؤسسة الجديد',
    'admin.enterNewName': 'أدخل اسم جديد',
    'admin.includeEmployees': 'تضمين الموظفين',
    'admin.includeEmployeesHelp': 'سيتم نسخ الموظفين بمعرفات جديدة. ستتم المحافظة على جميع التعيينات ومراجع المدراء.',
    'admin.excludeEmployeesHelp': 'سيتم نسخ هيكل المؤسسة فقط (الشعب، الأقسام، الوحدات، الوظائف). بدون موظفين أو تعيينات.',
    'admin.cloning': 'جاري النسخ...',
    'admin.cloneAction': 'نسخ المؤسسة',
    'admin.roleUser': 'مستخدم',
    'admin.roleOrgAdmin': 'مدير المؤسسة',
    'admin.roleViewer': 'مشاهد',
    'admin.rolePlatformAdmin': 'مدير المنصة',
    'admin.created': 'أُنشئ',
    'admin.updated': 'تم التحديث',
  },
};

export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<Language>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return (stored as Language) || 'he';
  });

  const direction = language === 'en' ? 'ltr' : 'rtl';

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, language);
    document.documentElement.dir = direction;
    document.documentElement.lang = language;
  }, [language, direction]);

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
  };

  const t = (key: string, params?: Record<string, string | number>): string => {
    let text = translations[language][key] || translations['en'][key] || key;

    if (params) {
      Object.entries(params).forEach(([paramKey, value]) => {
        text = text.replace(new RegExp(`\\{${paramKey}\\}`, 'g'), String(value));
      });
    }

    return text;
  };

  return (
    <LocaleContext.Provider value={{ language, setLanguage, t, direction }}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

export default LocaleContext;
